import { useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useGenres } from "../books/useGenres";
import { Spinner } from "../Spinner";
import { useOnboardingSurvey } from "./api";
import { OnboardingSurveyForm } from "./OnboardingSurveyForm";

interface OnboardingSurveyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "auto" when the gate opened it unbidden, "menu" when the user asked for it. */
  source: "auto" | "menu";
}

/**
 * The "soft jail": prominent, but always escapable.
 *
 * Dismiss semantics are deliberate. Esc and the close button shut it locally with no
 * network write, so an accidental keystroke never costs three days; only the explicit
 * "Remind me in N days" button snoozes it server-side. An outside click does nothing at
 * all, because a stray click should not dismiss something we would like answered.
 *
 * It stays *modal* despite being non-blocking: a non-modal dialog leaves a floating panel
 * of unknown provenance in the tab order of the page behind it, which is worse for screen
 * reader users than a focus trap they can leave with one keystroke.
 */
export function OnboardingSurveyDialog({
  open,
  onOpenChange,
  source,
}: OnboardingSurveyDialogProps) {
  const introRef = useRef<HTMLDivElement>(null);
  const survey = useOnboardingSurvey(open);
  const genres = useGenres();

  // Both, not just the survey: Q3's options are the live genre rows, and hydrating the
  // stored answers against an empty list would quietly drop every genre the user picked.
  const ready = survey.data && genres.data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[85svh] overflow-y-auto"
        onOpenAutoFocus={(event) => {
          // Radix focuses the first control, which drops a screen reader into the middle of
          // a form it never asked for. Start at the heading instead; the next Tab reaches Q1.
          event.preventDefault();
          introRef.current?.focus();
        }}
        onCloseAutoFocus={(event) => {
          // Radix restores focus to whatever was focused before opening. For the auto-open
          // that is <body> on a cold load, which strands the virtual cursor. When the user
          // opened it from the menu, Radix's own restore is right.
          if (source !== "auto") return;
          event.preventDefault();
          // By id rather than a ref: this provider lives above the router, and <main> is
          // RootLayout's, already tabIndex={-1} and already the app's route-change focus
          // target. A ref threaded down from here would only re-plumb the same element.
          document
            .getElementById("main-content")
            ?.focus({ preventScroll: true });
        }}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader ref={introRef} tabIndex={-1} className="outline-none">
          <DialogTitle>A couple of questions about you</DialogTitle>
          <DialogDescription>
            This helps us tailor the support we offer. Nothing here changes how
            the app works, and you can update your answers any time from the
            account menu.
          </DialogDescription>
        </DialogHeader>

        {ready ? (
          <OnboardingSurveyForm
            // Re-seeds cleanly on a fresh open rather than fighting a hydration effect.
            key={survey.data.answeredAt ?? "new"}
            state={survey.data}
            genreNames={genres.data.map((genre) => genre.name)}
            source={source}
            onDone={() => onOpenChange(false)}
          />
        ) : (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
